// Shared rules for the per-city listing pages /[lang]/[city]/events and
// /[lang]/[city]/tours. Framework-free so the build-time prefetch
// (the app's list-prefetch.ts), generateStaticParams and the screens all apply the same
// thresholds and compute the same numbers — same role as the app's
// boat-rental.ts and wine-tours.ts for their landing pages.

import { SITE } from '@site';

import type { DbEvent } from './supabase';
import type { Tour } from './tours';

/**
 * A city gets its own /[city]/events page only with at least this many
 * upcoming events — below it the page would be thin content competing with
 * the city guide (/[city]) for the same queries. The same floor makes a page
 * `noindex` if it ever renders with fewer.
 */
export const MIN_EVENTS_PER_CITY_PAGE = 10;

/** Same as MIN_EVENTS_PER_CITY_PAGE, for /[city]/tours. */
export const MIN_TOURS_PER_CITY_PAGE = 10;

/** How many soonest events the /[city]/events page shows as full cards; every event is still linked from the list below. */
export const TOP_EVENTS_ON_CITY_PAGE = 12;

/** Which cities have an exported /[city]/events and /[city]/tours page. */
export type CityListingPages = { events: string[]; tours: string[] };

function cityIdsWithAtLeast(rows: { city_id: string | null }[], min: number): string[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.city_id) counts.set(row.city_id, (counts.get(row.city_id) ?? 0) + 1);
  }
  return SITE.cityIds.filter((id) => (counts.get(id) ?? 0) >= min);
}

/** City ids (in SITE.cityIds order) with enough upcoming events for their own page. */
export function cityEventsPageIds(events: Pick<DbEvent, 'city_id'>[]): string[] {
  return cityIdsWithAtLeast(events, MIN_EVENTS_PER_CITY_PAGE);
}

/** City ids (in SITE.cityIds order) with enough tours for their own page. */
export function cityToursPageIds(tours: Pick<Tour, 'city_id'>[]): string[] {
  return cityIdsWithAtLeast(tours, MIN_TOURS_PER_CITY_PAGE);
}

/**
 * Whether a city's /[city]/events page exists, judged from the city landing
 * page's own seed (cityEventsQuery — a 24-row, 180-day slice of the same RPC
 * the page lists in full). The slice can only undercount the page, so a `true`
 * here never points at an unexported page; a `false` just falls back to the
 * regional /events hub. Used where the exact page list isn't baked (the
 * event detail pages' "see all" link).
 */
export function sliceHasCityEventsPage(slice: unknown[]): boolean {
  return slice.length >= MIN_EVENTS_PER_CITY_PAGE;
}

/** Tours counterpart of sliceHasCityEventsPage — exact here, the slice has no date window. */
export function sliceHasCityToursPage(slice: unknown[]): boolean {
  return slice.length >= MIN_TOURS_PER_CITY_PAGE;
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Lower-cased French articles/prepositions kept small when prettifying an ALL-CAPS venue name. */
const SMALL_WORDS = new Set(['de', 'des', 'du', 'la', 'le', 'les', 'et', 'sur', 'en', 'au', 'aux']);

/**
 * The feed spells the same venue several ways ("THEATRE LINO VENTURA",
 * "Palais Nikaia" / "Palais Nikaïa"). ALL-CAPS names are title-cased for
 * display; mixed-case ones are left as the feed wrote them.
 */
export function prettyVenue(name: string): string {
  const trimmed = name.replace(/\s+/g, ' ').trim();
  if (trimmed !== trimmed.toUpperCase()) return trimmed;
  return trimmed
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      if (index > 0 && SMALL_WORDS.has(word)) return word;
      // Regnal numbers stay upper-case: "Théâtre Alexandre III".
      if (index > 0 && /^[ivx]+$/.test(word)) return word.toUpperCase();
      // "l'alphabet" → "L'Alphabet"
      return word.replace(/(^|['’-])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
    })
    .join(' ');
}

/** Grouping key for a venue: case- and accent-insensitive. */
function venueKey(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Tie-break by code point, not localeCompare: these lists render during the
 * export (Node) and again during hydration (the browser), whose ICU collation
 * can differ — a reordered list would be a hydration mismatch.
 */
function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export type EventCityStats = {
  total: number;
  /** category_id (null → 'other'), most events first. */
  categories: { id: string; count: number }[];
  /** Venues, most events first; each under its most common spelling, prettified. */
  venues: { name: string; count: number }[];
  /** Cheapest listed starting price, EUR. */
  minPrice: number | null;
  /** start_ts of the first and last listed event. */
  firstStart: string | null;
  lastStart: string | null;
};

export function eventCityStats(events: DbEvent[]): EventCityStats {
  const categories = new Map<string, number>();
  const venues = new Map<string, { spellings: Map<string, number>; count: number }>();
  let minPrice: number | null = null;

  for (const event of events) {
    const category = event.category_id ?? 'other';
    categories.set(category, (categories.get(category) ?? 0) + 1);

    if (event.venue_name) {
      const key = venueKey(event.venue_name);
      const entry = venues.get(key) ?? { spellings: new Map<string, number>(), count: 0 };
      entry.count += 1;
      entry.spellings.set(event.venue_name, (entry.spellings.get(event.venue_name) ?? 0) + 1);
      venues.set(key, entry);
    }

    // Only EUR — the one currency on the feed; mixing would make the "from" wrong.
    if (event.min_price != null && event.min_price > 0 && (event.currency ?? SITE.currency) === SITE.currency) {
      minPrice = minPrice == null ? event.min_price : Math.min(minPrice, event.min_price);
    }
  }

  return {
    total: events.length,
    categories: [...categories.entries()]
      .map(([id, count]) => ({ id, count }))
      .sort((a, b) => b.count - a.count || cmp(a.id, b.id)),
    venues: [...venues.values()]
      .map(({ spellings, count }) => {
        const [spelling] = [...spellings.entries()].sort(
          (a, b) => b[1] - a[1] || cmp(a[0], b[0]),
        )[0];
        return { name: prettyVenue(spelling), count };
      })
      .sort((a, b) => b.count - a.count || cmp(a.name, b.name)),
    minPrice,
    // The RPC orders by start_ts.
    firstStart: events[0]?.start_ts ?? null,
    lastStart: events[events.length - 1]?.start_ts ?? null,
  };
}

// ---------------------------------------------------------------------------
// Tours
// ---------------------------------------------------------------------------

export type DurationBucket = 'short' | 'halfDay' | 'fullDay' | 'unknown';

/** Display order of the duration groups on /[city]/tours. */
export const DURATION_BUCKETS: DurationBucket[] = ['short', 'halfDay', 'fullDay', 'unknown'];

/**
 * Upper bound of a Viator duration string, in minutes: "8 h" → 480,
 * "3 h 30 min" → 210, "5 h – 6 h" → 360, "from 3 h" → 180, "45 min" → 45.
 * Null when there's nothing to parse.
 *
 * The string is sync-viator's formatDuration() of the raw payload, so this is
 * the same rule as tour_duration_minutes() in SQL
 * (20261012120000_hub_filters.sql): a tour lands in the same bucket in the
 * /[city]/tours groups and on both filters (/tours hub, /[city]/tours). The
 * upper bound means "up to 3 hours" never lists a tour that can run longer.
 */
export function durationMinutes(duration: string | null): number | null {
  if (!duration) return null;
  const parts = duration.split(/[–-]/);
  const last = parts[parts.length - 1];
  const hours = last.match(/(\d+(?:[.,]\d+)?)\s*h/i);
  const minutes = last.match(/(\d+)\s*min/i);
  if (!hours && !minutes) return null;
  return Math.round(
    (hours ? Number(hours[1].replace(',', '.')) * 60 : 0) + (minutes ? Number(minutes[1]) : 0),
  );
}

/** Up to 3 h → short, up to 6 h → half day, longer → full day (TOUR_DURATIONS in hub-filters.ts). */
export function durationBucket(duration: string | null): DurationBucket {
  const minutes = durationMinutes(duration);
  if (minutes == null) return 'unknown';
  if (minutes <= 180) return 'short';
  if (minutes <= 360) return 'halfDay';
  return 'fullDay';
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return Math.round(sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2);
}

export type TourCityStats = {
  total: number;
  /** Cheapest and median per-person price, EUR. */
  minPrice: number | null;
  medianPrice: number | null;
  /** Review-weighted average rating, one decimal. */
  avgRating: number | null;
  reviewCount: number;
  /** Tours per duration bucket (see durationBucket). */
  durations: Record<DurationBucket, number>;
};

export function tourCityStats(tours: Tour[]): TourCityStats {
  const prices = tours
    .filter((tour) => tour.from_price != null && (tour.currency ?? SITE.currency) === SITE.currency)
    .map((tour) => tour.from_price!);
  let weighted = 0;
  let reviewCount = 0;
  const durations: Record<DurationBucket, number> = { short: 0, halfDay: 0, fullDay: 0, unknown: 0 };
  for (const tour of tours) {
    durations[durationBucket(tour.duration)] += 1;
    if (tour.rating != null && tour.review_count) {
      weighted += tour.rating * tour.review_count;
      reviewCount += tour.review_count;
    }
  }
  return {
    total: tours.length,
    minPrice: prices.length ? Math.round(Math.min(...prices)) : null,
    medianPrice: median(prices),
    avgRating: reviewCount ? Math.round((weighted / reviewCount) * 10) / 10 : null,
    reviewCount,
    durations,
  };
}
